import { Box, Loader, Text, UnstyledButton } from "@mantine/core";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import Center from "~/components/Center";
import useCameraImageSrc from "~/hooks/Camera/useCameraImageSrc";
import { LooseObject } from "~/utils/Types";
import dayjs from "~/utils/dayjs";

import NoImagePlaceholder from "../NoImagePlaceholder";
import { resolveImageIdentity } from "./imageIdentity";

/**
 * Tile geometry. `ImageTileGrid` derives its virtualised row height from these,
 * so a tile must not grow beyond them — every caption line is clamped.
 *
 * The aspect is the image area only. 3:4 keeps a median 86% of a real keyframe
 * under `object-fit: cover`, within two points of the best available ratio: the
 * corpus runs 0.33–1.37 with a median of 0.68, so no fixed ratio avoids
 * cropping and the curve is flat either side of 3:4.
 */
export const TILE_IMAGE_ASPECT = 3 / 4;
export const TILE_CAPTION_HEIGHT = 44;

const formatScore = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }
  return typeof value === "string" && value !== "" ? value : undefined;
};

export type ImageTileProps = {
  item: LooseObject;
  bodyIdKey: string;
  dateKey: string;
  idKey: string;
  imgUrlKey: string;
  keyframeHashKey: string;
  locationKey: string;
  /** Row key behind the headline number. Omitted renders no badge. */
  scoreKey?: string;
  /** Resolved column label, surfaced as the badge's hover title. */
  scoreLabel?: string;
  onClick: (item: LooseObject) => void;
  renderOverlay?: (item: LooseObject) => ReactNode;
};

/**
 * One image card: cropped keyframe above a fixed caption. The whole card is the
 * click target.
 */
const ImageTile = ({
  item,
  bodyIdKey,
  dateKey,
  idKey,
  imgUrlKey,
  keyframeHashKey,
  locationKey,
  scoreKey,
  scoreLabel,
  onClick,
  renderOverlay,
}: ImageTileProps) => {
  const { t } = useTranslation("camera");
  const { src, isLoading, hasError, onError } = useCameraImageSrc({
    item,
    imgUrlKey,
    keyframeHashKey,
    locationKey,
  });

  const { barcode, bodyId } = resolveImageIdentity({ bodyIdKey, idKey, item });
  const label =
    bodyId === undefined
      ? barcode
      : t("imagesModal.tileBody", { value: bodyId });

  const captured = dayjs(item[dateKey]);
  const time = captured.isValid() ? captured.format("HH:mm:ss") : "";
  const score =
    scoreKey === undefined ? undefined : formatScore(item[scoreKey]);

  return (
    <Box component="li" style={{ minWidth: 0, minHeight: 0, height: "100%" }}>
      <UnstyledButton
        onClick={() => onClick(item)}
        aria-label={label}
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          textAlign: "left",
          backgroundColor: "var(--mantine-color-bg-primary)",
          border: "1px solid var(--mantine-color-border-secondary)",
          borderRadius: "var(--mantine-radius-md)",
        }}
      >
        {/* Fills whatever the caption leaves rather than re-asserting
            TILE_IMAGE_ASPECT: the grid already sized the row from that ratio,
            and a second CSS source would drift against it by a subpixel and
            break the uniform tile height. */}
        <Box
          style={{
            position: "relative",
            width: "100%",
            flex: "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
            backgroundColor: "var(--mantine-color-bg-secondary)",
          }}
        >
          {src && !hasError ? (
            <img
              onDragStart={(event) => event.preventDefault()}
              src={src}
              alt={label}
              loading="lazy"
              onError={onError}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : isLoading ? (
            <Center h="100%">
              <Loader size="sm" />
            </Center>
          ) : (
            // No retry affordance: the card itself is the button, and a nested
            // one is invalid. A failed load still recovers — `onError` triggers
            // the silent auth refresh, and opening the tile refetches full-res.
            <NoImagePlaceholder minHeight="100%" />
          )}
          {renderOverlay && (
            <Box style={{ position: "absolute", top: 4, left: 4 }}>
              {renderOverlay(item)}
            </Box>
          )}
        </Box>

        <Box
          style={{
            alignItems: "center",
            display: "flex",
            flex: "0 0 auto",
            gap: 8,
            height: TILE_CAPTION_HEIGHT,
            justifyContent: "space-between",
            padding: "0 10px",
          }}
        >
          <Box style={{ minWidth: 0 }}>
            {/* Truncates from the front because the identity is the tail
                either way: whole plants share a barcode prefix (every rgfoods
                code starts "MEQ-MANUALBARCODE-"), and a body number's prefix is
                the word "Body". An end ellipsis would drop the distinguishing
                half of both. */}
            <Text size="sm" fw={600} truncate="start">
              {label}
            </Text>
            <Text size="xs" c="var(--mantine-color-text-secondary)" truncate>
              {time}
            </Text>
          </Box>
          {score !== undefined && (
            <Text
              component="span"
              size="xs"
              title={scoreLabel}
              style={{
                backgroundColor: "var(--mantine-color-bg-secondary)",
                borderRadius: "var(--mantine-radius-sm)",
                flex: "0 0 auto",
                padding: "2px 8px",
              }}
            >
              {score}
            </Text>
          )}
        </Box>
      </UnstyledButton>
    </Box>
  );
};

export default ImageTile;
